import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Typography,
    Card,
    CardContent,
    LinearProgress,
    CircularProgress,
    Chip,
    Icon,
    Paper,
    Box,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Tooltip,
    Popover
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import * as Actions from '../../store/actions';
import MetricHistoryChart from '../widgets/MetricHistoryChart';

const useStyles = makeStyles(theme => ({
    dialogPaper: {
        minHeight: '70vh',
        maxHeight: '90vh',
        position: 'relative'
    },
    chartContainer: {
        height: 200,
        marginTop: theme.spacing(2)
    },
    metricCard: {
        textAlign: 'center',
        padding: theme.spacing(2)
    },
    configSection: {
        marginBottom: theme.spacing(2)
    },
    volumeChip: {
        margin: theme.spacing(0.5),
        cursor: 'pointer'
    },
    runningIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: '#ff9800'
    },
    runningIndicatorContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(0.5, 2),
        borderRadius: theme.spacing(1),
        fontSize: '0.875rem'
    },
    multipleTestsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        maxWidth: '400px',
        overflowX: 'auto',
        overflowY: 'hidden',
        '&::-webkit-scrollbar': {
            height: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.3)',
            borderRadius: '2px',
        }
    },
    singleTestIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: theme.spacing(0.5, 1.5),
        borderRadius: theme.spacing(0.5),
        fontSize: '0.8rem',
        minWidth: '160px',
        maxWidth: '180px',
        whiteSpace: 'nowrap',
        flexShrink: 0
    },
    testTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center'
    },
    runningSpinner: {
        color: 'white',
        animation: '$spin 2s linear infinite'
    },
    runningText: {
        fontWeight: 'bold',
        fontSize: '0.875rem'
    },
    runningSubtext: {
        fontSize: '0.75rem',
        opacity: 0.8
    },
    '@keyframes spin': {
        '0%': {
            transform: 'rotate(0deg)'
        },
        '100%': {
            transform: 'rotate(360deg)'
        }
    },
    errorText: {
        color: theme.palette.error.main
    },
    successText: {
        color: theme.palette.success.main
    },
    popover: {
        pointerEvents: 'none'
    },
    popoverPaper: {
        padding: theme.spacing(2),
        maxWidth: 300,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`
    },
    infoIcon: {
        fontSize: '16px',
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        marginLeft: theme.spacing(1),
        '&:hover': {
            color: theme.palette.primary.main
        }
    },
    dialogHeader: {
        backgroundColor: theme.palette.grey[100],
        padding: theme.spacing(1, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '48px',
        gap: theme.spacing(2)
    },
    closeButton: {
        padding: theme.spacing(1),
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1302,
        backdropFilter: 'blur(2px)'
    },
    loadingContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(2)
    }
}));

const IOPSStressDialog = ({ open, onClose, clusterId }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    
    // Redux state
    const clusterState = useSelector(({ clusters }) => clusters?.cluster || {});
    const {
        stressTests = {
            data: null,
            history: null,
            volumes: null,
            loading: false,
            error: null
        },
        clusterMetrics = { data: null }
    } = clusterState;
    
    const {
        data: stressTestData,
        history: stressTestHistory,
        volumes: clusterVolumes,
        loading: stressTestLoading,
        error: stressTestError
    } = stressTests;
    
    const { data: metricsData } = clusterMetrics;

    // Local state
    const [selectedNode, setSelectedNode] = useState('');
    const [selectedVolume, setSelectedVolume] = useState('');
    const [availableVolumes, setAvailableVolumes] = useState([]);
    const [volumeLoading, setVolumeLoading] = useState(false);
    const [testDuration, setTestDuration] = useState(60);
    const [blockSize, setBlockSize] = useState('4k');
    const [ioPattern, setIoPattern] = useState('random');
    const [readWriteRatio, setReadWriteRatio] = useState(70);
    const [testIntensity, setTestIntensity] = useState('medium');
    const [testMode, setTestMode] = useState('quick'); // 'quick' or 'comprehensive'
    const [runningTests, setRunningTests] = useState(new Map()); // Map of nodeId -> {nodeName, volumeName, startTime, testConfig}
    const [connectionStatus, setConnectionStatus] = useState('connected'); // connected, degraded, failed
    const [pollingErrors, setPollingErrors] = useState(0);
    const [lastDataUpdate, setLastDataUpdate] = useState(Date.now()); // Force re-render trigger
    const [nodeDataLoading, setNodeDataLoading] = useState(false); // Loading state for node/volume switching
    // Popover state
    const [anchorEl, setAnchorEl] = useState(null);
    const [activePopover, setActivePopover] = useState(null);
    
    // Ref to track if component is mounted
    const isMountedRef = useRef(true);
    
    // Animation state for smooth gauge transitions
    const [animatedMetrics, setAnimatedMetrics] = useState({
        totalIOPS: 0,
        readIOPS: 0,
        writeIOPS: 0,
        latency: 0,
        bandwidth: 0
    });
    const animationFrameRef = useRef(null);
    const animationStartTime = useRef(null);

    // Configuration options
    const blockSizes = ['4k', '8k', '16k', '32k', '64k', '128k', '1m'];
    const ioPatterns = [
        { value: 'random', label: 'Random' },
        { value: 'sequential', label: 'Sequential' },
        { value: 'mixed', label: 'Mixed (75% random, 25% sequential)' }
    ];
    const intensityLevels = [
        { value: 'light', label: 'Light (25%)', description: 'Low impact, safe for production' },
        { value: 'medium', label: 'Medium (50%)', description: 'Moderate load' },
        { value: 'heavy', label: 'Heavy (75%)', description: 'High load' },
        { value: 'maximum', label: 'Maximum (100%)', description: 'Maximum stress' }
    ];
    const testModes = [
        { value: 'quick', label: 'Quick Test', description: 'Single test job - duration as specified (~1 minute)' },
        { value: 'comprehensive', label: 'Comprehensive Test', description: 'Multiple test jobs - takes 6x longer (~6-8 minutes)' }
    ];

    // Load data when dialog opens and reset connection status
    useEffect(() => {
        // Mark component as mounted
        isMountedRef.current = true;
        
        if (open && clusterId) {
            // Reset connection status when opening dialog
            setConnectionStatus('connected');
            setPollingErrors(0);
            
            // Force fresh data fetch with cache busting
            dispatch(Actions.fetchClusterVolumes(clusterId));
            
            const initialParams = { 
                testType: 'disk', 
                limit: 50,
                // Don't include nodes parameter on initial load to get all node data
                _cacheBust: Date.now() // Force fresh data
            };
            dispatch(Actions.fetchStressTestHistory(clusterId, initialParams));
        } else if (!open) {
            // Clear running tests when dialog is closed
            setRunningTests(new Map());
        }

        // Cleanup function to prevent memory leaks
        return () => {
            // Mark component as unmounted
            isMountedRef.current = false;
            
            // Clear running tests when component unmounts
            if (!open) {
                setRunningTests(new Map());
            }
        };
    }, [open, clusterId, dispatch]);



    // Force refresh when node selection changes to get latest data
    useEffect(() => {
        if (selectedNode && clusterId && open) {
            // Set loading state when switching nodes
            setNodeDataLoading(true);
            
            // Clear old test history data immediately to prevent stale data display
            dispatch(Actions.setStressTestHistory(null));
            
            // Reset local state to prevent stale data from previous node
            setLastDataUpdate(Date.now());
            
            const nodeChangeParams = { 
                testType: 'disk', 
                limit: 50,
                nodes: [selectedNode], // Include specific node to get fresh data for that node
                _cacheBust: Date.now() // Force fresh data
            };
            
            Promise.all([
                dispatch(Actions.fetchStressTestHistory(clusterId, nodeChangeParams)),
                dispatch(Actions.fetchNodeVolumes(clusterId, selectedNode, { testType: 'disk' }))
                    .then(response => {
                        // Response is now directly the volumes array
                        if (response && Array.isArray(response)) {
                            setAvailableVolumes(response);
                            // Auto-select the first volume if user hasn't selected one
                            if (!selectedVolume && response.length > 0) {
                                setSelectedVolume(response[0].path);
                            }
                        }
                        return response;
                    })
                    .catch(error => {
                        console.error('Failed to fetch volumes for node:', error);
                        setAvailableVolumes([]);
                        throw error;
                    })
            ]).finally(() => {
                // Clear loading state when both requests complete
                setNodeDataLoading(false);
                setVolumeLoading(false);
            });
            
        } else {
            // Clear volume selection when no node selected
            setAvailableVolumes([]);
            setSelectedVolume('');
            // Also clear test history when no node is selected
            dispatch(Actions.setStressTestHistory(null));
            setNodeDataLoading(false);
        }
    }, [selectedNode, clusterId, open, dispatch]);

    // Load volume-specific test data when both node and volume are selected
    useEffect(() => {
        if (selectedNode && selectedVolume && clusterId && open) {
            // Set loading state when switching volumes
            setNodeDataLoading(true);
            
            // Clear old data before fetching volume-specific data
            dispatch(Actions.setStressTestHistory(null));
            
            const volumeParams = {
                testType: 'disk',
                limit: 50,
                nodes: [selectedNode],
                volume: selectedVolume,
                _cacheBust: Date.now()
            };
            
            
            dispatch(Actions.fetchStressTestHistory(clusterId, volumeParams))
                .catch(error => {
                    console.error(`[IOPSStressDialog] Error fetching volume data:`, error);
                })
                .finally(() => {
                    setNodeDataLoading(false);
                });
        }
    }, [selectedVolume, selectedNode, clusterId, open, dispatch]);

    // Force component re-render when stress test history changes
    // This ensures getCurrentMetrics is recalculated when new test results arrive
    useEffect(() => {
        if (stressTestHistory && selectedNode) {
            // If we have a selected node, validate we have data for it
            const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
            
            // Handle both old and new data structures
            let nodeRecords = [];
            if (historyData?.[selectedNode] && Array.isArray(historyData[selectedNode])) {
                // Old structure - direct node data
                nodeRecords = historyData[selectedNode];
            } else if (historyData?.summary?.history && Array.isArray(historyData.summary.history)) {
                // New structure - filter summary.history array
                nodeRecords = historyData.summary.history.filter(record => 
                    record.hostname === selectedNode || record.node === selectedNode
                );
            }
            
            
            if (nodeRecords?.length > 0) {
                const latestRecord = nodeRecords[nodeRecords.length - 1];
            }
            
            // Trigger component re-render by updating state
            setLastDataUpdate(Date.now());
        } else if (stressTestHistory && !selectedNode) {
            // Still trigger re-render for cluster-wide data
            setLastDataUpdate(Date.now());
        }
    }, [stressTestHistory, selectedNode]);

    // Animation effect - watch for metrics changes and animate to new values
    useEffect(() => {
        const currentMetrics = getCurrentMetrics();
        
        // Only animate if we have meaningful changes and component is mounted
        if (isMountedRef.current) {
            const hasSignificantChange = 
                Math.abs(currentMetrics.totalIOPS - animatedMetrics.totalIOPS) > 10 ||
                Math.abs(currentMetrics.readIOPS - animatedMetrics.readIOPS) > 5 ||
                Math.abs(currentMetrics.writeIOPS - animatedMetrics.writeIOPS) > 5 ||
                Math.abs(currentMetrics.latency - animatedMetrics.latency) > 0.1 ||
                Math.abs(currentMetrics.bandwidth - animatedMetrics.bandwidth) > 5;

            if (hasSignificantChange) {
                // Different durations: faster for going to 0, normal speed for increasing
                const isResetting = currentMetrics.totalIOPS === 0 && currentMetrics.readIOPS === 0;
                const duration = isResetting ? 600 : 1200; // Much faster for reset (0.6s), slower for increases (1.2s)
                
                animateToNewValues({
                    totalIOPS: currentMetrics.totalIOPS,
                    readIOPS: currentMetrics.readIOPS,
                    writeIOPS: currentMetrics.writeIOPS,
                    latency: currentMetrics.latency,
                    bandwidth: currentMetrics.bandwidth
                }, duration);
            }
        }
    }, [lastDataUpdate, selectedNode, selectedVolume]); // Depend on data update triggers

    // Cleanup animation on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    // Get available nodes
    const getAvailableNodes = () => {
        if (!clusterVolumes?.volumesByNode) return [];
        
        return Object.keys(clusterVolumes.volumesByNode)
            .filter(nodeName => clusterVolumes.volumesByNode[nodeName].nodeStatus === 'online')
            .sort();
    };

    // Get volumes for selected node (from organized test results)
    const getNodeVolumes = () => {
        return availableVolumes || [];
    };

    // Popover handlers
    const handlePopoverOpen = (event, popoverId) => {
        setAnchorEl(event.currentTarget);
        setActivePopover(popoverId);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setActivePopover(null);
    };



    // Manual refresh function for when automatic polling fails
    const handleManualRefresh = async () => {
        if (!selectedNode) {
            return;
        }

        try {
            await dispatch(Actions.fetchStressTestHistory(clusterId, { 
                testType: 'disk', 
                limit: 50,
                nodes: selectedNode ? [selectedNode] : undefined, // Include specific node if selected
                _cacheBust: Date.now() // Force fresh data
            }));
            
            // Reset connection status on successful manual refresh (only if mounted)
            if (isMountedRef.current && connectionStatus !== 'connected') {
                setConnectionStatus('connected');
                setPollingErrors(0);
            }
            
            // Force component re-render to show refreshed data
            if (isMountedRef.current) {
                setLastDataUpdate(Date.now());
            }
        } catch (error) {
            console.error('Manual refresh failed:', error);
            
            // Only update state if component is still mounted
            if (isMountedRef.current) {
                const errorCount = pollingErrors + 1;
                setPollingErrors(errorCount);
                
                if (errorCount >= 3) {
                    setConnectionStatus('failed');
                } else {
                    setConnectionStatus('degraded');
                }
            }
        }
    };

    // Animation function for smooth gauge transitions
    const animateToNewValues = (targetMetrics, duration = 2000) => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        const startValues = { ...animatedMetrics };
        animationStartTime.current = performance.now();
        
        const animate = (currentTime) => {
            if (!isMountedRef.current) return;
            
            const elapsed = currentTime - animationStartTime.current;
            const progress = Math.min(elapsed / duration, 1);
            
            // Different easing for increasing vs decreasing values (like car tachometer)
            const isDecreasing = Object.keys(targetMetrics).some(key => 
                targetMetrics[key] < startValues[key]
            );
            
            let easedProgress;
            if (isDecreasing) {
                // Smooth deceleration for decreasing values (like releasing gas pedal)
                easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
            } else {
                // Slightly bouncy for increasing values (like pressing gas pedal)
                easedProgress = progress < 0.5 
                    ? 2 * progress * progress 
                    : 1 - Math.pow(-2 * progress + 2, 2) / 2; // Ease-in-out
            }
            
            const newAnimatedMetrics = {};
            Object.keys(targetMetrics).forEach(key => {
                const start = startValues[key] || 0;
                const end = targetMetrics[key];
                newAnimatedMetrics[key] = start + (end - start) * easedProgress;
            });
            
            setAnimatedMetrics(newAnimatedMetrics);
            
            if (progress < 1) {
                animationFrameRef.current = requestAnimationFrame(animate);
            }
        };
        
        animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Execute stress test
    const handleExecuteTest = async () => {
        if (!selectedNode || !selectedVolume) {
            return;
        }

        // Check if a test is already running on this node
        if (runningTests.has(selectedNode)) {
            return;
        }

        // Validate numeric inputs and use defaults if invalid
        const validDuration = typeof testDuration === 'number' && testDuration >= 10 && testDuration <= 600 ? testDuration : 60;
        const validReadWriteRatio = typeof readWriteRatio === 'number' && readWriteRatio >= 0 && readWriteRatio <= 100 ? readWriteRatio : 70;

        const testConfig = {
            testType: 'iops_stress',
            nodes: [selectedNode],
            duration: validDuration,
            intensity: testIntensity,
            options: {
                volumeMount: selectedVolume,
                fileSize: '1G',
                ioPattern,
                blockSize,
                readWriteRatio: validReadWriteRatio,
                quickMode: testMode === 'quick'
            }
        };

        // Add this test to running tests
        const newRunningTests = new Map(runningTests);
        newRunningTests.set(selectedNode, {
            nodeName: selectedNode,
            volumeName: selectedVolume,
            startTime: new Date(),
            testConfig: testConfig,
            connectionStatus: 'connected'
        });
        setRunningTests(newRunningTests);

        try {
            // Clear existing results for this node before starting new test
            setPollingErrors(0);
            setConnectionStatus('connected');
            
            // Clear Redux state to remove old test data from UI
            dispatch(Actions.clearStressTestData());

            // Animate gauges to 0 quickly when starting new test
            animateToNewValues({
                totalIOPS: 0,
                readIOPS: 0,
                writeIOPS: 0,
                latency: 0,
                bandwidth: 0
            }, 800); // Faster 0.8 second animation to 0

            // Immediately refresh cluster volumes to keep node dropdown populated
            // Use a small delay to ensure the clear operation completes first
            setTimeout(() => {
                dispatch(Actions.fetchClusterVolumes(clusterId));
            }, 100);

            
            const testResult = await dispatch(Actions.executeStressTest(clusterId, testConfig));

            if (testResult?.error || testResult?.payload?.error) {
                console.error('Backend error:', testResult.error || testResult.payload.error);
            }
            
            
            // Check for backend database issues
            if (testResult?.payload?.message?.includes("Cluster") && testResult?.payload?.message?.includes("not found")) {
                // Remove this test from running tests since we can't proceed
                setRunningTests(prevRunningTests => {
                    const updatedRunningTests = new Map(prevRunningTests);
                    updatedRunningTests.delete(selectedNode);
                    return updatedRunningTests;
                });
                return;
            }
            
            // Store the timestamp when test started for detecting new results
            const testStartTime = moment();
            
            // Intelligent polling strategy - poll until we get fresh results for this specific node
            let pollingAttempts = 0;
            const maxPollingMinutes = 15; // Maximum 15 minutes of polling
            const maxPollingAttempts = Math.floor((maxPollingMinutes * 60) / 15); // Poll every 15 seconds
            
            const smartRefresh = async () => {
                try {

                    // Store current data for comparison
                    const beforeHistoryData = stressTestHistory?.data?.history || stressTestHistory?.history;
                    const beforeRecordCount = beforeHistoryData?.[selectedNode]?.length || 0;
                    
                    // Try to match initial load parameters - include the specific node we want
                    const historyResult = await dispatch(Actions.fetchStressTestHistory(clusterId, { 
                        testType: 'disk', 
                        limit: 50,
                        nodes: [selectedNode], // Include specific node to get node-specific data instead of summary
                        _cacheBust: Date.now() // Force fresh data during test
                    }));
                    
                    // Check if data changed - try multiple data paths
                    let afterHistoryData = null;
                    
                    // Try different possible data paths
                    if (historyResult?.payload?.data?.history) {
                        afterHistoryData = historyResult.payload.data.history;
                    } else if (historyResult?.data?.history) {
                        afterHistoryData = historyResult.data.history;
                    } else if (historyResult?.history) {
                        afterHistoryData = historyResult.history;
                    } else {
                    }
                    
                    const afterRecordCount = afterHistoryData?.[selectedNode]?.length || 0;

                    // If we have history data but not for our selected node, log all available nodes
                    if (afterHistoryData && !afterHistoryData[selectedNode]) {
                        // Check if any node has recent data
                        Object.keys(afterHistoryData).forEach(nodeKey => {
                            if (afterHistoryData[nodeKey]?.length > 0) {
                                const latestEntry = afterHistoryData[nodeKey][afterHistoryData[nodeKey].length - 1];
                                const entryTime = moment(latestEntry.timestamp);
                                const minutesAgo = moment().diff(entryTime, 'minutes');
                            }
                        });
                    }
                    
                    if (afterHistoryData && afterHistoryData[selectedNode]?.length > 0) {
                        const latestEntry = afterHistoryData[selectedNode][afterHistoryData[selectedNode].length - 1];
                        const entryTime = moment(latestEntry.timestamp);
                        const minutesAgo = moment().diff(entryTime, 'minutes');
                    }
                    
                    pollingAttempts++;
                    
                    // Reset connection status on success (only if component is still mounted)
                    if (isMountedRef.current && connectionStatus !== 'connected') {
                        setConnectionStatus('connected');
                        setPollingErrors(0);
                    }
                } catch (error) {
                    // Only update state if component is still mounted
                    if (!isMountedRef.current) {
                        return false;
                    }
                    
                    const errorCount = pollingErrors + 1;
                    setPollingErrors(errorCount);
                    
                    // Update connection status based on error count
                    if (errorCount === 1) {
                        setConnectionStatus('degraded');
                    } else if (errorCount >= 3) {
                        setConnectionStatus('failed');
                    }
                    
                    console.warn(`Polling attempt failed (${errorCount} errors):`, error.message);
                    
                    // Check if it's a socket error specifically
                    if (error.message?.includes('socket hang up') || error.message?.includes('ECONNRESET')) {
                        console.warn('Socket connection issue detected, extending polling interval');
                    }
                    
                    return false;
                }
                return true;
            };

            // Start intelligent polling that checks for test completion using the new endpoint
            let currentInterval = 10000; // Start with 10 seconds
            let testCompleted = false;
            
            const checkForResults = async () => {
                try {
                    
                    // Check if stress test results file exists and has fresh data
                    const checkResult = await dispatch(Actions.checkStressTestResults(clusterId, { 
                        nodes: [selectedNode],
                        since: testStartTime.toISOString()
                    }));
                    
                    if (checkResult?.data?.freshResults && checkResult?.data?.nodes) {
                        const nodeResult = checkResult.data.nodes[selectedNode];
                        if (nodeResult?.hasResults && nodeResult?.latestTimestamp) {
                            const resultTime = moment(nodeResult.latestTimestamp);
         
                            // Fetch the actual results
                            await dispatch(Actions.fetchStressTestHistory(clusterId, { 
                                testType: 'disk', 
                                limit: 50,
                                nodes: [selectedNode],
                                _cacheBust: Date.now() 
                            }));
                            
                            // Refresh cluster volumes to ensure node dropdown stays populated
                            await dispatch(Actions.fetchClusterVolumes(clusterId));
                            
                            // Remove this test from running tests since it completed
                            if (isMountedRef.current) {
                                setRunningTests(prevRunningTests => {
                                    const updatedRunningTests = new Map(prevRunningTests);
                                    updatedRunningTests.delete(selectedNode);
                                    return updatedRunningTests;
                                });
                                setLastDataUpdate(Date.now());
                            }
                            
                            testCompleted = true;
                            return true;
                        } 
                    } 
                    return false;
                } catch (error) {
                    console.error('Error checking for results:', error);
                    return false;
                }
            };
            
            const scheduleNextCheck = () => {
                if (!testCompleted && pollingAttempts < maxPollingAttempts && isMountedRef.current) {
                    setTimeout(async () => {
                        pollingAttempts++;
                        
                        const completed = await checkForResults();
                        
                        if (!completed && !testCompleted) {
                            scheduleNextCheck();
                        }
                    }, currentInterval);
                } else if (pollingAttempts >= maxPollingAttempts && !testCompleted) {
                    
                    // Refresh cluster volumes even on timeout to prevent dropdown from going blank
                    dispatch(Actions.fetchClusterVolumes(clusterId));
                    
                    if (isMountedRef.current) {
                        // Remove this test from running tests due to timeout
                        setRunningTests(prevRunningTests => {
                            const updatedRunningTests = new Map(prevRunningTests);
                            updatedRunningTests.delete(selectedNode);
                            return updatedRunningTests;
                        });
                    }
                }
            };

            scheduleNextCheck();

        } catch (error) {
            console.error('Failed to execute stress test:', error);
            
            // Refresh cluster volumes on error to prevent dropdown from going blank
            dispatch(Actions.fetchClusterVolumes(clusterId));
            
            // Remove this test from running tests due to error
            if (isMountedRef.current) {
                setRunningTests(prevRunningTests => {
                    const updatedRunningTests = new Map(prevRunningTests);
                    updatedRunningTests.delete(selectedNode);
                    return updatedRunningTests;
                });
            }
        }
    };

    // Get current IOPS metrics - prioritize latest test results
    const getCurrentMetrics = () => {
        // Return default metrics if no node is selected
        if (!selectedNode) {
            return { readIOPS: 0, writeIOPS: 0, latency: 0, totalIOPS: 0, bandwidth: 0, isFromTest: false };
        }
        
        // First check if we have recent stress test results
        const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
        
        if (historyData && selectedNode) {
            // Handle new data structure where results are in summary.history array
            let nodeTestRecords = [];
            
            // Check if we have the old structure (direct node data)
            if (historyData[selectedNode] && Array.isArray(historyData[selectedNode]) && historyData[selectedNode].length > 0) {
                nodeTestRecords = historyData[selectedNode];
            }
            // Check if we have the new structure (summary with history array)
            else if (historyData.summary?.history && Array.isArray(historyData.summary.history) && historyData.summary.history.length > 0) {
                // Filter the summary history array for the selected node
                nodeTestRecords = historyData.summary.history.filter(record => 
                    record.hostname === selectedNode || record.node === selectedNode
                );
            }
            
            if (nodeTestRecords.length > 0) {
                
                // If a volume is selected, filter records by volume path
                if (selectedVolume) {
                    const originalCount = nodeTestRecords.length;
                    
                    nodeTestRecords = nodeTestRecords.filter(record => {
                        // Check various possible locations for volume information
                        const volumePath = record.volumeMount || 
                                         record.parameters?.volumeMount ||
                                         record.config?.volumeMount ||
                                         record.options?.volumeMount ||
                                         record.volume ||
                                         record.mountPoint;
                        
                        return volumePath === selectedVolume;
                    });
                    
                    
                    // If no records match the selected volume, return empty metrics
                    if (nodeTestRecords.length === 0) {
                        return { readIOPS: 0, writeIOPS: 0, latency: 0, totalIOPS: 0, bandwidth: 0, isFromTest: false };
                    }
                }
                
                // Sort records by timestamp to ensure we get the most recent first
                nodeTestRecords.sort((a, b) => {
                    const timeA = moment(a.timestamp);
                    const timeB = moment(b.timestamp);
                    return timeB.diff(timeA); // Most recent first
                });
                
                // Find the most recent test record that has actual results
                let latestTest = null;
                
                // Look through records from most recent to oldest to find one with results
                for (let i = 0; i < nodeTestRecords.length; i++) {
                    const testRecord = nodeTestRecords[i];
                    const results = testRecord.results || testRecord.data || testRecord.payload || {};
                    
                    // Check if this record has meaningful IOPS data
                    const hasIOPSData = !!(
                        results.totalIOPS || 
                        results.readIOPS || 
                        results.writeIOPS || 
                        results.readOperations || 
                        results.writeOperations ||
                        testRecord.totalIOPS ||
                        testRecord.readIOPS ||
                        testRecord.writeIOPS ||
                        testRecord.readOperations ||
                        testRecord.writeOperations
                    );
                    
                    
                    if (hasIOPSData) {
                        latestTest = testRecord;
                        break;
                    }
                }
                
                // Fallback to most recent record if none have results
                if (!latestTest) {
                    latestTest = nodeTestRecords[0]; // First in sorted array = most recent
                }
        
                // Check multiple possible locations for results data
                const results = latestTest.results || latestTest.data || latestTest.payload || {};
           
                // If the test is recent (within last 2 hours), use its results
                const testTime = moment(latestTest.timestamp);
                const now = moment();
                const minutesAgo = now.diff(testTime, 'minutes');
                
                
                // Handle new JSON format (enhanced HNA v2.9+) with direct IOPS values
                const totalIOPS = results.totalIOPS || latestTest.totalIOPS || 0;
                const readIOPS = results.readIOPS || latestTest.readIOPS || 0;
                const writeIOPS = results.writeIOPS || latestTest.writeIOPS || 0;
                

                // Fallback: if read/write not available but total is, calculate from ratio
                let finalReadIOPS = readIOPS;
                let finalWriteIOPS = writeIOPS;

                // Only use fallback if both read and write are zero (shouldn't happen with real data)
                if (totalIOPS > 0 && readIOPS === 0 && writeIOPS === 0) {
                    // Try multiple locations for readWriteRatio
                    const readRatio = (
                        results.parameters?.readWriteRatio || 
                        latestTest.parameters?.readWriteRatio ||
                        results.readWriteRatio ||
                        latestTest.readWriteRatio ||
                        70  // default to 70% read
                    ) / 100;
                    
                    finalReadIOPS = Math.round(totalIOPS * readRatio);
                    finalWriteIOPS = Math.round(totalIOPS * (1 - readRatio));
                } 
                
                // Temporarily show older data (8 hours) to display something while debugging test execution
                if (minutesAgo <= 480 && totalIOPS > 0) {
                    // Get latency in microseconds and convert to milliseconds for display
                    const latencyMicros = results.avgLatency || results.averageLatency || latestTest.avgLatency || latestTest.averageLatency || 0;
                    const latencyMillis = latencyMicros / 1000; // Convert microseconds to milliseconds
                    
                    const metrics = {
                        readIOPS: finalReadIOPS,
                        writeIOPS: finalWriteIOPS,
                        latency: latencyMillis,
                        totalIOPS: totalIOPS,
                        bandwidth: results.totalBandwidth || latestTest.totalBandwidth || 
                                  ((results.readBandwidth || latestTest.readBandwidth || 0) + (results.writeBandwidth || latestTest.writeBandwidth || 0)) || 0,
                        isFromTest: true,
                        dataAge: minutesAgo // Track how old the data is
                    };

                    return metrics;
                } 
            } 
        } 
        // Fallback to live metrics if available
        if (metricsData?.nodeMetrics && selectedNode) {
            const nodeMetrics = metricsData.nodeMetrics.find(node => 
                (node.hostname || node.nodeName || node.name) === selectedNode
            );

            if (nodeMetrics?.system?.disk) {
                const diskMetrics = nodeMetrics.system.disk;
                return {
                    readIOPS: diskMetrics.totalReads || diskMetrics.readIOPS || 0,
                    writeIOPS: diskMetrics.totalWrites || diskMetrics.writeIOPS || 0,
                    latency: diskMetrics.averageLatency || diskMetrics.avgLatency || 0,
                    totalIOPS: diskMetrics.totalIOPS || (diskMetrics.totalReads || 0) + (diskMetrics.totalWrites || 0),
                    bandwidth: 0,
                    isFromTest: false
                };
            }
        }

        return { readIOPS: 0, writeIOPS: 0, latency: 0, totalIOPS: 0, bandwidth: 0, isFromTest: false };
    };

    // Use lastDataUpdate to ensure metrics are recalculated when data changes
    const currentMetrics = React.useMemo(() => {
        return getCurrentMetrics();
    }, [stressTestHistory, selectedNode, selectedVolume, metricsData, lastDataUpdate]);
    
    // Initialize animated metrics with current values on first load
    useEffect(() => {
        if (open && currentMetrics && isMountedRef.current) {
            // Only initialize if animated metrics are at zero (first time)
            const isFirstLoad = animatedMetrics.totalIOPS === 0 && 
                               animatedMetrics.readIOPS === 0 && 
                               animatedMetrics.writeIOPS === 0;
                               
            if (isFirstLoad) {
                setAnimatedMetrics({
                    totalIOPS: currentMetrics.totalIOPS,
                    readIOPS: currentMetrics.readIOPS,
                    writeIOPS: currentMetrics.writeIOPS,
                    latency: currentMetrics.latency,
                    bandwidth: currentMetrics.bandwidth
                });
            }
        }
    }, [open, currentMetrics]);
    
    const nodeVolumes = getNodeVolumes();
    const availableNodes = getAvailableNodes();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            classes={{ paper: classes.dialogPaper }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1300
                }
            }}
            PaperProps={{
                style: {
                    zIndex: 1301
                }
            }}
        >
            {/* Loading Overlay */}
            {nodeDataLoading && (
                <div className={classes.loadingOverlay}>
                    <div className={classes.loadingContent}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" color="textSecondary">
                            Loading node data...
                        </Typography>
                    </div>
                </div>
            )}
            
            {/* Custom Header Bar */}
            <div className={classes.dialogHeader}>
                {/* Left side: Title and connection status */}
                <div className="flex items-center">
                    <Icon className="mr-8 text-brown">storage</Icon>
                    <Typography variant="h6">
                        IOPS Stress Test
                        {runningTests.size > 0 && (
                            <span style={{ color: '#1976d2', marginLeft: '8px', fontSize: '0.8em' }}>
                                ({runningTests.size} running)
                            </span>
                        )}
                    </Typography>
                    
                    {/* Connection Status Indicator */}
                    {runningTests.size > 0 && connectionStatus !== 'connected' && (
                        <Icon 
                            className="ml-8" 
                            color={connectionStatus === 'degraded' ? 'warning' : 'error'}
                            title={connectionStatus === 'degraded' ? 
                                'Connection issues - using reduced refresh rate' : 
                                'Connection failed - real-time updates unavailable'
                            }
                        >
                            {connectionStatus === 'degraded' ? 'signal_wifi_4_bar' : 'signal_wifi_off'}
                        </Icon>
                    )}
                </div>
                
                {/* Right side: Running tests and close button */}
                <div className="flex items-center gap-4">
                    {/* Running Tests Status Indicators */}
                    {runningTests.size > 0 && (
                        <div className={classes.multipleTestsContainer}>
                            {Array.from(runningTests.entries()).map(([nodeId, testInfo]) => (
                                <div key={nodeId} className={classes.singleTestIndicator}>
                                    <CircularProgress 
                                        size={14} 
                                        className={classes.runningSpinner}
                                    />
                                    <div className={classes.testTextContainer}>
                                        <div className={classes.runningText}>
                                            {testInfo.nodeName}
                                        </div>
                                        <div className={classes.runningSubtext}>
                                            {testInfo.volumeName.split('/').pop()} • {testInfo.testConfig.duration}s
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Close Button */}
                    <IconButton 
                        className={classes.closeButton}
                        onClick={onClose}
                        size="small"
                    >
                        <Icon>close</Icon>
                    </IconButton>
                </div>
            </div>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Configuration Section */}
                    <Grid item xs={12} md={4}>
                        <Paper className="p-16">
                            <Typography variant="h6" className="mb-16">
                                Test Configuration
                            </Typography>

                            <div className={classes.configSection}>
                                <FormControl fullWidth size="small" margin="normal">
                                    <InputLabel>Target Node</InputLabel>
                                    <Select
                                        value={selectedNode}
                                        onChange={(e) => setSelectedNode(e.target.value)}
                                        label="Target Node"
                                    >
                                        {availableNodes.map(nodeName => (
                                            <MenuItem key={nodeName} value={nodeName}>
                                                {nodeName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {selectedNode && (
                                    <FormControl fullWidth size="small" margin="normal">
                                        <InputLabel>Volume</InputLabel>
                                        <Select
                                            value={selectedVolume}
                                            onChange={(e) => setSelectedVolume(e.target.value)}
                                            label="Volume"
                                            disabled={volumeLoading}
                                        >
                                            {volumeLoading ? (
                                                <MenuItem disabled>
                                                    <Typography variant="body2">Loading volumes...</Typography>
                                                </MenuItem>
                                            ) : nodeVolumes.length === 0 ? (
                                                <MenuItem disabled>
                                                    <Typography variant="body2">No test results found</Typography>
                                                </MenuItem>
                                            ) : (
                                                nodeVolumes.map(volume => (
                                                    <MenuItem key={volume.path} value={volume.path}>
                                                        <div>
                                                            <Typography variant="body2">{volume.path}</Typography>
                                                            <Typography variant="caption" color="textSecondary">
                                                                {volume.device} • {volume.size}
                                                                {volume.testCount > 0 && (
                                                                    <span style={{ color: '#4caf50', marginLeft: '8px' }}>
                                                                        • {volume.testCount} tests • Last: {
                                                                            new Date(volume.lastTest).toLocaleString()
                                                                        }
                                                                    </span>
                                                                )}
                                                            </Typography>
                                                        </div>
                                                    </MenuItem>
                                                ))
                                            )}
                                        </Select>
                                    </FormControl>
                                )}
                            </div>

                            <Divider className="my-16" />

                            <div className={classes.configSection}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Duration (seconds)"
                                            type="number"
                                            value={testDuration}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    setTestDuration('');
                                                } else {
                                                    // Allow any numeric input during editing
                                                    const numValue = parseInt(value);
                                                    if (!isNaN(numValue)) {
                                                        setTestDuration(numValue);
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || isNaN(parseInt(value))) {
                                                    setTestDuration(60);
                                                } else {
                                                    const numValue = parseInt(value);
                                                    // Apply range validation only on blur
                                                    if (numValue < 10) {
                                                        setTestDuration(10);
                                                    } else if (numValue > 600) {
                                                        setTestDuration(600);
                                                    }
                                                    // If within range, keep the value as is
                                                }
                                            }}
                                            inputProps={{ min: 10, max: 600 }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Block Size</InputLabel>
                                            <Select
                                                value={blockSize}
                                                onChange={(e) => setBlockSize(e.target.value)}
                                                label="Block Size"
                                            >
                                                {blockSizes.map(size => (
                                                    <MenuItem key={size} value={size}>{size}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>I/O Pattern</InputLabel>
                                            <Select
                                                value={ioPattern}
                                                onChange={(e) => setIoPattern(e.target.value)}
                                                label="I/O Pattern"
                                            >
                                                {ioPatterns.map(pattern => (
                                                    <MenuItem key={pattern.value} value={pattern.value}>
                                                        {pattern.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Read/Write Ratio (%)"
                                            type="number"
                                            value={readWriteRatio}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '') {
                                                    setReadWriteRatio('');
                                                } else {
                                                    const numValue = parseInt(value);
                                                    if (!isNaN(numValue)) {
                                                        setReadWriteRatio(Math.min(100, Math.max(0, numValue)));
                                                    }
                                                }
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                                                    setReadWriteRatio(70);
                                                }
                                            }}
                                            inputProps={{ min: 0, max: 100 }}
                                            helperText={`${readWriteRatio || 0}% read, ${100-(readWriteRatio || 0)}% write`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Test Intensity</InputLabel>
                                                <Select
                                                    value={testIntensity}
                                                    onChange={(e) => setTestIntensity(e.target.value)}
                                                    label="Test Intensity"
                                                >
                                                    {intensityLevels.map(level => (
                                                        <MenuItem key={level.value} value={level.value}>
                                                            {level.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Icon 
                                                className={classes.infoIcon}
                                                style={{ marginBottom: '8px' }}
                                                onMouseEnter={(e) => handlePopoverOpen(e, 'intensity-info')}
                                                onMouseLeave={handlePopoverClose}
                                            >
                                                info
                                            </Icon>
                                            <Popover
                                                className={classes.popover}
                                                classes={{ paper: classes.popoverPaper }}
                                                open={Boolean(anchorEl) && activePopover === 'intensity-info'}
                                                anchorEl={anchorEl}
                                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                onClose={handlePopoverClose}
                                                disableAutoFocus={true}
                                                disableEnforceFocus={true}
                                                disableRestoreFocus={true}
                                            >
                                                <div>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                        Test Intensity Levels
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '6px' }}>
                                                        <strong>Light (25%):</strong> I/O Depth: 16, Jobs: 2 - Low impact, safe for production
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '6px' }}>
                                                        <strong>Medium (50%):</strong> I/O Depth: 32, Jobs: 4 - Moderate load
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '6px' }}>
                                                        <strong>Heavy (75%):</strong> I/O Depth: 64, Jobs: 8 - High load
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        <strong>Maximum (100%):</strong> I/O Depth: 128, Jobs: 16 - Maximum stress
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary" style={{ marginTop: '8px', display: 'block' }}>
                                                        Higher intensity = more concurrent I/O operations and parallel jobs
                                                    </Typography>
                                                </div>
                                            </Popover>
                                        </div>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Test Mode</InputLabel>
                                                <Select
                                                    value={testMode}
                                                    onChange={(e) => setTestMode(e.target.value)}
                                                    label="Test Mode"
                                                >
                                                    {testModes.map(mode => (
                                                        <MenuItem key={mode.value} value={mode.value}>
                                                            <div>
                                                                <Typography variant="body2">{mode.label}</Typography>
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {mode.description}
                                                                </Typography>
                                                            </div>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Icon 
                                                className={classes.infoIcon}
                                                style={{ marginBottom: '8px' }}
                                                onMouseEnter={(e) => handlePopoverOpen(e, 'mode-info')}
                                                onMouseLeave={handlePopoverClose}
                                            >
                                                info
                                            </Icon>
                                            <Popover
                                                className={classes.popover}
                                                classes={{ paper: classes.popoverPaper }}
                                                open={Boolean(anchorEl) && activePopover === 'mode-info'}
                                                anchorEl={anchorEl}
                                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                                onClose={handlePopoverClose}
                                                disableAutoFocus={true}
                                                disableEnforceFocus={true}
                                                disableRestoreFocus={true}
                                            >
                                                <div>
                                                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                                        Test Mode Comparison
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '8px' }}>
                                                        <strong>Quick Test:</strong>
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '12px', marginBottom: '6px' }}>
                                                        • Single FIO job using your settings
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '12px', marginBottom: '8px' }}>
                                                        • Duration: ~{typeof testDuration === 'number' ? testDuration : 60} seconds as specified
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginBottom: '8px' }}>
                                                        <strong>Comprehensive Test:</strong>
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '12px', marginBottom: '3px' }}>
                                                        • 6 sequential FIO jobs:
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '20px', marginBottom: '2px' }}>
                                                        1. Max Random Read IOPS
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '20px', marginBottom: '2px' }}>
                                                        2. Max Random Write IOPS
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '20px', marginBottom: '2px' }}>
                                                        3. Mixed Read/Write Workload
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '20px', marginBottom: '2px' }}>
                                                        4. Sequential Read Bandwidth
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '20px', marginBottom: '2px' }}>
                                                        5. Sequential Write Bandwidth
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '20px', marginBottom: '6px' }}>
                                                        6. Latency Test
                                                    </Typography>
                                                    <Typography variant="body2" style={{ marginLeft: '12px' }}>
                                                        • Duration: 6-8 minutes total
                                                    </Typography>
                                                </div>
                                            </Popover>
                                        </div>
                                    </Grid>
                                </Grid>
                            </div>
                        </Paper>
                    </Grid>

                    {/* Speedtest-Style IOPS Test Display */}
                    <Grid item xs={12} md={8}>
                        <Paper className="p-16 -mt-20">
                            {/* Speedtest-Style Gauges */}
                            <Grid container spacing={3}>
                                {/* Main IOPS Gauge */}
                                <Grid item xs={12} md={6}>
                                    <Card className="p-16 text-center" style={{ minHeight: '212px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
                                            <Typography variant="h6">Total IOPS</Typography>
                                            <Typography variant="caption" style={{ 
                                                color: currentMetrics.isFromTest ? '#4caf50' : '#ff9800',
                                                fontSize: '10px',
                                                fontWeight: 'bold'
                                            }}>
                                                {currentMetrics.isFromTest ? 
                                                    `STRESS TEST ${currentMetrics.dataAge ? `(${currentMetrics.dataAge}m ago)` : ''}` : 
                                                    'LIVE MONITORING'
                                                }
                                            </Typography>
                                        </div>
                                        <div className="relative">
                                            <svg width="180" height="100" viewBox="0 0 180 100" className="mx-auto">
                                                {/* Background Arc */}
                                                <path
                                                    d="M 20 80 A 70 70 0 0 1 160 80"
                                                    stroke="#e0e0e0"
                                                    strokeWidth="8"
                                                    fill="none"
                                                />
                                                {/* Progress Arc */}
                                                <path
                                                    d="M 20 80 A 70 70 0 0 1 160 80"
                                                    stroke="#9c27b0"
                                                    strokeWidth="8"
                                                    fill="none"
                                                    strokeDasharray={`${Math.min(100, (animatedMetrics.totalIOPS / 50000) * 100) * 2.2} 220`}
                                                    style={{ transition: 'stroke-dasharray 0.1s ease' }}
                                                />
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center" style={{ top: '0px' }}>
                                                <div>
                                                    <Typography 
                                                        className="text-purple font-bold"
                                                        style={{
                                                            fontSize: Math.round(animatedMetrics.totalIOPS) >= 100000 ? '2.5rem' : Math.round(animatedMetrics.totalIOPS) >= 10000 ? '2.7rem' : '3.0rem'
                                                        }}
                                                    >
                                                        {Math.round(animatedMetrics.totalIOPS).toLocaleString()}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        IOPS
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </Grid>

                                {/* Secondary Metrics */}
                                <Grid item xs={12} md={6}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant='h5' className="text-blue font-bold">
                                                    {Math.round(animatedMetrics.readIOPS).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Read IOPS</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-blue-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.readIOPS / 30000) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                            <Typography variant='h5' className="text-green font-bold">

                                                    {Math.round(animatedMetrics.writeIOPS).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Write IOPS</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-green-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, (animatedMetrics.writeIOPS / 20000) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" className="text-orange font-bold">
                                                    {animatedMetrics.latency.toFixed(2)}
                                                </Typography>
                                                <Typography variant="caption">Latency (ms)</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-orange-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ width: `${Math.min(100, Math.max(10, 100 - (animatedMetrics.latency / 100)))}%` }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Card className="p-12 text-center" style={{ minHeight: '90px' }}>
                                                <Typography variant="h5" className="text-cyan font-bold">
                                                    {Math.round(animatedMetrics.bandwidth > 0 
                                                        ? animatedMetrics.bandwidth
                                                        : ((animatedMetrics.totalIOPS * 4) / 1024)
                                                    ).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption">Bandwidth (MB/s)</Typography>
                                                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-cyan-500 h-2 rounded-full transition-all duration-100"
                                                        style={{ 
                                                            width: `${Math.min(100, (
                                                                animatedMetrics.bandwidth > 0 
                                                                    ? (animatedMetrics.bandwidth / 500) * 100
                                                                    : ((animatedMetrics.totalIOPS * 4) / 1024 / 200) * 100
                                                            ))}%` 
                                                        }}
                                                    ></div>
                                                </div>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Total IOPS Historical Data Chart */}
                            <div className={classes.chartContainer}>
                                {selectedNode ? (
                                    <Paper className="p-16" style={{ height: '100%' }}>

{/* Enhanced stress test time-series chart */}
                                        {(() => {
                                            // Get stress test history for selected node
                                            const historyData = stressTestHistory?.data?.history || stressTestHistory?.history;
                                            let nodeTestRecords = [];
                                            
                                            if (historyData && selectedNode) {
                                                if (historyData[selectedNode]?.length > 0) {
                                                    nodeTestRecords = historyData[selectedNode];
                                                } else if (historyData.summary?.history?.length > 0) {
                                                    nodeTestRecords = historyData.summary.history.filter(record => 
                                                        record.hostname === selectedNode
                                                    );
                                                }
                                                
                                                // Filter by volume if one is selected
                                                if (selectedVolume && nodeTestRecords.length > 0) {
                                                    nodeTestRecords = nodeTestRecords.filter(record => {
                                                        const volumePath = record.volumeMount || 
                                                                         record.parameters?.volumeMount ||
                                                                         record.config?.volumeMount ||
                                                                         record.options?.volumeMount ||
                                                                         record.volume ||
                                                                         record.mountPoint;
                                                        return volumePath === selectedVolume;
                                                    });
                                                }
                                            }

                                            // Find the most recent test with time-series data (by timestamp, not array position)
                                            let latestTestWithTimeSeries = null;
                                            let latestTimestamp = null;
                                            
                                            for (let i = 0; i < nodeTestRecords.length; i++) {
                                                const record = nodeTestRecords[i];
                                                const results = record.results || record.data || record.payload || {};
                                                const recordTimestamp = new Date(record.timestamp);
                                                
                                                if ((results.timeSeriesData || results.timeSeries) && 
                                                    Array.isArray(results.timeSeriesData || results.timeSeries) && 
                                                    (results.timeSeriesData || results.timeSeries).length > 0) {
                                                    
                                                    if (!latestTimestamp || recordTimestamp > latestTimestamp) {
                                                        latestTestWithTimeSeries = record;
                                                        latestTimestamp = recordTimestamp;
                                                    }
                                                }
                                            }

                                            // If we have time-series data from the latest test, show detailed chart
                                            if (latestTestWithTimeSeries) {
                                                const results = latestTestWithTimeSeries.results || latestTestWithTimeSeries.data || latestTestWithTimeSeries.payload || {};
                                                const timeSeries = results.timeSeriesData || results.timeSeries || [];
                                                const testDate = moment(latestTestWithTimeSeries.timestamp).format('MMM DD, HH:mm');


                                                return (
                                                    <div style={{ height: '250px', position: 'relative' }}>
                                                        <Typography variant="subtitle2" style={{ color: '#666', fontSize: '16px', marginBottom: '16px', marginTop: '10px' }}>
                                                            IOPS Stress Test Time-Series Data - Test at {testDate} ({timeSeries.length} points)
                                                        </Typography>

                                                        <canvas 
                                                            ref={(canvas) => {
                                                                if (canvas && timeSeries.length > 0) {
                                                                    const ctx = canvas.getContext('2d');
                                                                    
                                                                    // Set canvas size
                                                                    canvas.width = canvas.offsetWidth;
                                                                    canvas.height = canvas.offsetHeight;
                                                                    
                                                                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                                                                    
                                                                    // Chart dimensions
                                                                    const padding = { top: 20, right: 80, bottom: 40, left: 60 };
                                                                    const chartWidth = canvas.width - padding.left - padding.right;
                                                                    const chartHeight = canvas.height - padding.top - padding.bottom;
                                                                    
                                                    // Prepare data - map from new JSON structure
                                                    // New structure: { iops: totalIOPS, bandwidth: MB/s, latency: microseconds, timestamp: seconds }
                                                    const iopsSeries = timeSeries.map(point => point.iops || 0);
                                                    
                                                    // Calculate read/write split from parameters (default 70/30 if not available)
                                                    const readRatio = (results.parameters?.readWriteRatio || 70) / 100;
                                                    const writeRatio = 1 - readRatio;
                                                    const readSeries = timeSeries.map(point => Math.round((point.iops || 0) * readRatio));
                                                    const writeSeries = timeSeries.map(point => Math.round((point.iops || 0) * writeRatio));
                                                    
                                                    const cpuSeries = timeSeries.map(point => point.cpuUsage || 0);
                                                    const latencySeries = timeSeries.map(point => point.latency || 0);
                                                    const bandwidthSeries = timeSeries.map(point => point.bandwidth || 0);
                                                            
                                                                    // Calculate scales
                                                                    const maxIOPS = Math.max(...iopsSeries, ...readSeries, ...writeSeries);
                                                                    const maxCPU = Math.max(...cpuSeries);
                                                                    const maxLatency = Math.max(...latencySeries);
                                                                    
                                                                    // Check if we have any real IOPS data
                                                                    const hasRealIOPSData = maxIOPS > 0;
                                                                    
                                                                    // Draw background
                                                                    ctx.fillStyle = '#fafafa';
                                                                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                                                                    
                                                                    // If no real IOPS data, show explanatory message
                                                                    if (!hasRealIOPSData) {
                                                                        ctx.fillStyle = '#ffffff';
                                                                        ctx.fillRect(padding.left, padding.top, chartWidth, chartHeight);
                                                                        
                                                                        ctx.fillStyle = '#ff9800';
                                                                        ctx.font = 'bold 14px Arial';
                                                                        ctx.textAlign = 'center';
                                                                        ctx.fillText('⚠️ Time-Series Shows System Monitoring (All IOPS = 0)', canvas.width / 2, canvas.height / 2 - 20);
                                                                        
                                                                        ctx.fillStyle = '#666';
                                                                        ctx.font = '12px Arial';
                                                                        ctx.fillText('FIO status monitoring is available but IOPS still = 0', canvas.width / 2, canvas.height / 2 + 5);
                                                                        ctx.fillText('The actual test IOPS results are shown in the gauges above', canvas.width / 2, canvas.height / 2 + 25);
                                                                        
                                                                        // Still draw CPU data if available
                                                                        if (maxCPU > 0) {
                                                                            ctx.fillStyle = '#4caf50';
                                                                            ctx.font = '11px Arial';
                                                                            const avgCPU = cpuSeries.reduce((a, b) => a + b, 0) / cpuSeries.length;
                                                                            ctx.fillText(`✓ CPU monitoring worked: Average ${avgCPU.toFixed(1)}% during test`, canvas.width / 2, canvas.height / 2 + 45);
                                                                        }
                                                                        
                                                                        return;
                                                                    }
                                                                    
                                                                    // We have real IOPS data! Show success message
                                                                    ctx.fillStyle = '#4caf50';
                                                                    ctx.font = 'bold 12px Arial';
                                                                    ctx.textAlign = 'center';
                                                                    ctx.fillText('FIO Time-Series Monitoring', canvas.width / 2, 20);
                                                                    
                                                                    // Draw chart area
                                                                    ctx.fillStyle = '#ffffff';
                                                                    ctx.fillRect(padding.left, padding.top, chartWidth, chartHeight);
                                                                    
                                                                    // Draw grid lines
                                                                    ctx.strokeStyle = '#e0e0e0';
                                                                    ctx.lineWidth = 1;
                                                                    for (let i = 0; i <= 5; i++) {
                                                                        const y = padding.top + (chartHeight * i / 5);
                                                                        ctx.beginPath();
                                                                        ctx.moveTo(padding.left, y);
                                                                        ctx.lineTo(padding.left + chartWidth, y);
                                                                        ctx.stroke();
                                                                        
                                                                        // Y-axis labels (IOPS)
                                                                        ctx.fillStyle = '#666';
                                                                        ctx.font = '10px Arial';
                                                                        ctx.textAlign = 'right';
                                                                        const iopsValue = Math.round(maxIOPS * (5 - i) / 5);
                                                                        ctx.fillText(iopsValue.toLocaleString(), padding.left - 5, y + 3);
                                                                    }
                                                                    
                                                                    // Draw time axis
                                                                    for (let i = 0; i <= 10; i++) {
                                                                        const x = padding.left + (chartWidth * i / 10);
                                                                        ctx.strokeStyle = '#f0f0f0';
                                                                        ctx.beginPath();
                                                                        ctx.moveTo(x, padding.top);
                                                                        ctx.lineTo(x, padding.top + chartHeight);
                                                                        ctx.stroke();
                                                                        
                                                                        // Time labels
                                                                        ctx.fillStyle = '#666';
                                                                        ctx.font = '9px Arial';
                                                                        ctx.textAlign = 'center';
                                                                        const timeIndex = Math.floor((timeSeries.length - 1) * i / 10);
                                                                        if (timeSeries[timeIndex]) {
                                                                            const elapsed = timeSeries[timeIndex].timestamp || timeIndex;
                                                                            const timeLabel = `${Math.floor(elapsed / 60)}:${String(Math.floor(elapsed % 60)).padStart(2, '0')}`;
                                                                            ctx.fillText(timeLabel, x, padding.top + chartHeight + 15);
                                                                        }
                                                                    }
                                                                    
                                                                    // Helper function to draw line
                                                                    const drawLine = (data, color, lineWidth = 2, scale = maxIOPS) => {
                                                                        if (data.length < 2) return;
                                                                        
                                                                        ctx.strokeStyle = color;
                                                                        ctx.lineWidth = lineWidth;
                                                                        ctx.beginPath();
                                                                        
                                                                        data.forEach((value, index) => {
                                                                            const x = padding.left + (chartWidth * index / (data.length - 1));
                                                                            const y = padding.top + chartHeight - (chartHeight * value / scale);
                                                                            
                                                                            if (index === 0) {
                                                                                ctx.moveTo(x, y);
                                                                            } else {
                                                                                ctx.lineTo(x, y);
                                                                            }
                                                                        });
                                                                        ctx.stroke();
                                                                    };
                                                                    
                                                                    // Draw IOPS lines
                                                                    drawLine(iopsSeries, '#9c27b0', 3); // Total IOPS (thick purple)
                                                                    drawLine(readSeries, '#2196f3', 2); // Read IOPS (blue)
                                                                    drawLine(writeSeries, '#4caf50', 2); // Write IOPS (green)
                                                                    
                                                                    // Draw bandwidth line (scaled to IOPS range)
                                                                    const maxBandwidth = Math.max(...bandwidthSeries);
                                                                    if (maxBandwidth > 0) {
                                                                        const scaledBandwidth = bandwidthSeries.map(bw => (bw / maxBandwidth) * maxIOPS * 0.3); // Scale to 30% of IOPS range
                                                                        drawLine(scaledBandwidth, '#00bcd4', 1.5); // Bandwidth (cyan, thin)
                                                                    }
                                                                    
                                                                    // Draw secondary metrics (CPU - scaled to fit)
                                                                    if (maxCPU > 0) {
                                                                        const scaledCPU = cpuSeries.map(cpu => cpu * maxIOPS / 100); // Scale CPU to IOPS range
                                                                        drawLine(scaledCPU, '#ff9800', 1.5); // CPU (orange, thin)
                                                                    }
                                                                    
                                                                    // Axis labels
                                                                    ctx.fillStyle = '#333';
                                                                    ctx.font = 'bold 11px Arial';
                                                                    ctx.textAlign = 'left';
                                                                    ctx.fillText('IOPS', padding.left, 15);
                                                                    
                                                                    ctx.textAlign = 'center';
                                                                    ctx.fillText('Time (mm:ss)', canvas.width / 2, canvas.height - 5);
                                                                    
                                                                    // Legend
                                                                    const legendItems = [
                                                                        { color: '#9c27b0', label: 'Total IOPS', thickness: 3 },
                                                                        { color: '#2196f3', label: 'Read IOPS', thickness: 2 },
                                                                        { color: '#4caf50', label: 'Write IOPS', thickness: 2 }
                                                                    ];
                                                                    
                                                                    if (maxBandwidth > 0) {
                                                                        legendItems.push({ color: '#00bcd4', label: 'Bandwidth', thickness: 1.5 });
                                                                    }
                                                                    
                                                                    if (maxCPU > 0) {
                                                                        legendItems.push({ color: '#ff9800', label: 'CPU %', thickness: 1.5 });
                                                                    }
                                                                    
                                                                    legendItems.forEach((item, index) => {
                                                                        const legendY = 30 + (index * 16);
                                                                        const legendX = canvas.width - 70;
                                                                        
                                                                        ctx.strokeStyle = item.color;
                                                                        ctx.lineWidth = item.thickness;
                                                                        ctx.beginPath();
                                                                        ctx.moveTo(legendX, legendY);
                                                                        ctx.lineTo(legendX + 15, legendY);
                                                                        ctx.stroke();
                                                                        
                                                                        ctx.fillStyle = '#333';
                                                                        ctx.font = '10px Arial';
                                                                        ctx.textAlign = 'left';
                                                                        ctx.fillText(item.label, legendX + 20, legendY + 3);
                                                                    });
                                                                    
                                                                    // Performance summary
                                                                    ctx.fillStyle = '#666';
                                                                    ctx.font = '9px Arial';
                                                                    ctx.textAlign = 'left';
                                                                    const avgIOPS = Math.round(iopsSeries.reduce((a, b) => a + b, 0) / iopsSeries.length);
                                                                    const peakIOPS = Math.max(...iopsSeries);
                                                                    const avgBandwidth = Math.round(bandwidthSeries.reduce((a, b) => a + b, 0) / bandwidthSeries.length);
                                                                    const avgCPU = cpuSeries.length > 0 ? Math.round(cpuSeries.reduce((a, b) => a + b, 0) / cpuSeries.length) : 0;
                                                                    
                                                                    ctx.fillText(`Avg: ${avgIOPS.toLocaleString()} IOPS`, 10, canvas.height - 25);
                                                                    ctx.fillText(`Peak: ${peakIOPS.toLocaleString()} IOPS`, 10, canvas.height - 15);
                                                                    ctx.fillText(`BW: ${avgBandwidth} MB/s`, 10, canvas.height - 5);
                                                                    if (avgCPU > 0) {
                                                                        ctx.fillText(`CPU: ${avgCPU}%`, 10, canvas.height - 5);
                                                                    }
                                                                }
                                                            }}
                                                            style={{ width: '100%', height: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                );
                                            }
                                            
                                            // If we have stress test results but no time-series, show summary chart
                                            else if (nodeTestRecords.length > 0) {
                                                const labels = nodeTestRecords.slice(-10).map(record => 
                                                    moment(record.timestamp).format('MM/DD HH:mm')
                                                );
                                                
                                                const totalIOPSData = nodeTestRecords.slice(-10).map(record => {
                                                    const results = record.results || record.data || record.payload || {};
                                                    const readIOPS = results.readIOPS || results.readOperations || record.readIOPS || record.readOperations || 0;
                                                    const writeIOPS = results.writeIOPS || results.writeOperations || record.writeIOPS || record.writeOperations || 0;
                                                    return results.totalIOPS || record.totalIOPS || (readIOPS + writeIOPS);
                                                });

                                                return (
                                                    <div style={{ height: '200px' }}>
                                                        <Typography variant="subtitle2" style={{ color: '#666', fontSize: '12px', marginBottom: '8px' }}>
                                                            📈 Test Results History (Last {nodeTestRecords.length} tests) - Final IOPS values only
                                                        </Typography>
                                                        <canvas 
                                                            ref={(canvas) => {
                                                                if (canvas && totalIOPSData.length > 0) {
                                                                    const ctx = canvas.getContext('2d');
                                                                    
                                                                    canvas.width = canvas.offsetWidth;
                                                                    canvas.height = canvas.offsetHeight;
                                                                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                                                                    
                                                                    const padding = { top: 20, right: 50, bottom: 40, left: 60 };
                                                                    const chartWidth = canvas.width - padding.left - padding.right;
                                                                    const chartHeight = canvas.height - padding.top - padding.bottom;
                                                                    
                                                                    const maxIOPS = Math.max(...totalIOPSData);
                                                                    const minIOPS = Math.min(...totalIOPSData);
                                                                    const range = maxIOPS - minIOPS || 1;
                                                                    
                                                                    // Background
                                                                    ctx.fillStyle = '#fafafa';
                                                                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                                                                    ctx.fillStyle = '#ffffff';
                                                                    ctx.fillRect(padding.left, padding.top, chartWidth, chartHeight);
                                                                    
                                                                    // Grid and labels
                                                                    ctx.strokeStyle = '#e0e0e0';
                                                                    ctx.lineWidth = 1;
                                                                    for (let i = 0; i <= 5; i++) {
                                                                        const y = padding.top + (chartHeight * i / 5);
                                                                        ctx.beginPath();
                                                                        ctx.moveTo(padding.left, y);
                                                                        ctx.lineTo(padding.left + chartWidth, y);
                                                                        ctx.stroke();
                                                                        
                                                                        ctx.fillStyle = '#666';
                                                                        ctx.font = '10px Arial';
                                                                        ctx.textAlign = 'right';
                                                                        const value = Math.round(maxIOPS - (range * i / 5));
                                                                        ctx.fillText(value.toLocaleString(), padding.left - 5, y + 3);
                                                                    }
                                                                    
                                                                    // Draw line
                                                                    if (totalIOPSData.length > 1) {
                                                                        ctx.strokeStyle = '#9c27b0';
                                                                        ctx.lineWidth = 3;
                                                                        ctx.beginPath();
                                                                        
                                                                        totalIOPSData.forEach((iops, index) => {
                                                                            const x = padding.left + (chartWidth * index / (totalIOPSData.length - 1));
                                                                            const y = padding.top + chartHeight - (chartHeight * (iops - minIOPS) / range);
                                                                            
                                                                            if (index === 0) {
                                                                                ctx.moveTo(x, y);
                                                                            } else {
                                                                                ctx.lineTo(x, y);
                                                                            }
                                                                        });
                                                                        ctx.stroke();
                                                                        
                                                                        // Points
                                                                        ctx.fillStyle = '#9c27b0';
                                                                        totalIOPSData.forEach((iops, index) => {
                                                                            const x = padding.left + (chartWidth * index / (totalIOPSData.length - 1));
                                                                            const y = padding.top + chartHeight - (chartHeight * (iops - minIOPS) / range);
                                                                            
                                                                            ctx.beginPath();
                                                                            ctx.arc(x, y, 4, 0, 2 * Math.PI);
                                                                            ctx.fill();
                                                                        });
                                                                    }
                                                                    
                                                                    // Labels
                                                                    ctx.fillStyle = '#333';
                                                                    ctx.font = 'bold 11px Arial';
                                                                    ctx.textAlign = 'left';
                                                                    ctx.fillText('IOPS', padding.left, 15);
                                                                    
                                                                    ctx.textAlign = 'center';
                                                                    ctx.fillText('Test History', canvas.width / 2, canvas.height - 5);
                                                                }
                                                            }}
                                                            style={{ width: '100%', height: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}
                                                        />
                                                    </div>
                                                );
                                            }
                                            
                                            // Fallback to system monitoring chart
                                            return (
                                                <div>

                                                    <MetricHistoryChart
                                                        clusterId={clusterId}
                                                        metricType="iops"
                                                        title={`System Monitoring IOPS`}
                                                        unit="IOPS"
                                                        preSelectedNode={selectedNode}
                                                    />
                                                </div>
                                            );
                                        })()}
                                    </Paper>
                                ) : (
                                    <Paper className="p-16" style={{ height: '100%' }}>
                                        <Box 
                                            display="flex" 
                                            flexDirection="column"
                                            justifyContent="center" 
                                            alignItems="center" 
                                            height="100%"
                                            bgcolor="grey.50"
                                            className="rounded"
                                        >
                                            <Icon color="disabled" style={{ fontSize: 48, marginBottom: 16 }}>
                                                timeline
                                            </Icon>
                                            <Typography variant="h6" color="textSecondary" className="mb-8">
                                                Select a Node
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" className="text-center">
                                                Choose a target node to view IOPS performance data
                                            </Typography>
                                        </Box>
                                    </Paper>
                                )}
                            </div>
                        </Paper>
                    </Grid>

                    {/* Error Display */}
                    {stressTestError && (
                        <Grid item xs={12}>
                            <Paper className="p-16 bg-red-50">
                                <Typography variant="body2" className={classes.errorText}>
                                    <Icon className="mr-8">error</Icon>
                                    {stressTestError}
                                </Typography>
                            </Paper>
                        </Grid>
                    )}

                    {/* Connection Status Indicator */}
                    {runningTests.size > 0 && connectionStatus !== 'connected' && (
                        <Grid item xs={12}>
                            <Paper className={`p-16 ${connectionStatus === 'degraded' ? 'bg-yellow-50' : 'bg-red-50'}`}>
                                <Typography variant="body2" className="flex items-center">
                                    <Icon className="mr-8" color={connectionStatus === 'degraded' ? 'warning' : 'error'}>
                                        {connectionStatus === 'degraded' ? 'warning' : 'signal_wifi_off'}
                                    </Icon>
                                    {connectionStatus === 'degraded' ? 
                                        `Connection issues detected (${pollingErrors} failed attempts). Using reduced refresh rate.` :
                                        `Connection failed (${pollingErrors} errors). Real-time updates unavailable.`
                                    }
                                </Typography>
                            </Paper>
                        </Grid>
                    )}


                </Grid>

                {stressTestLoading && <LinearProgress className="mt-16" />}
            </DialogContent>

            {/* Action Buttons moved to dialog content */}
            <div style={{ padding: '16px 24px 24px', borderTop: '1px solid #e0e0e0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                {/* Manual Refresh Button - especially useful during connection issues */}
                <Button
                    onClick={handleManualRefresh}
                    color="default"
                    disabled={stressTestLoading}
                    startIcon={stressTestLoading ? <LinearProgress className="w-20" /> : <Icon>refresh</Icon>}
                    size="small"
                >
                    Refresh Data
                </Button>
                
                <Button
                    onClick={handleExecuteTest}
                    color="primary"
                    variant="contained"
                    disabled={!selectedNode || !selectedVolume || stressTestLoading || runningTests.has(selectedNode)}
                    startIcon={runningTests.has(selectedNode) ? <LinearProgress className="w-20" /> : <Icon>play_arrow</Icon>}
                >
                    {runningTests.has(selectedNode) ? 
                        `Test Running on ${selectedNode}` : 
                        `Start ${testMode === 'quick' ? 'Quick' : 'Comprehensive'} IOPS Test`
                    }
                </Button>
            </div>
        </Dialog>
    );
};

export default IOPSStressDialog;
